import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CustomNavbar from "../CustomPermissions/CustomNavbar.jsx";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import { exportRecordsToExcel } from "../../utils/exportUtils";

function Records() {
  const [data, setData] = useState(null);
  const [dailyEntryData, setDailyEntryData] = useState([]);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [pipelineandturnedup, setPipelineandturnedup] = useState([]);
  const [candidateRecords, setCandidateRecords] = useState({}); // Store fetched candidate records per user
  const [activeFilter, setActiveFilter] = useState({}); // Track active filter (resume_forwarded, pipeline, turned_up)
  const navigate = useNavigate();
  const [uniquePipelineCounts, setUniquePipelineCounts] = useState({});
  const [updateTargetUser, setUpdateTargetUser] = useState(null); // Track user to update target
  const [targetInput, setTargetInput] = useState('');
  const [revenueTargetInput, setRevenueTargetInput] = useState('');
  const [displayMode, setDisplayMode] = useState('target_candidates'); // 'target_candidates' or 'revenue'
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null); // Track user to delete
  const [editUser, setEditUser] = useState(null); // Track user to edit
  const [editName, setEditName] = useState('');
  const [editDesignation, setEditDesignation] = useState('');
  const [editImage, setEditImage] = useState(null);
  const [designations, setDesignations] = useState([]);

  // Handle permission-based navigation
  const handlePermissionNavigation = (userPermissions) => {
    if (userPermissions.includes("website.custom_viewer_perm")) {
      navigate("/viewers", {
        replace: true,
        state: { userPermissions: "custom_viewer_perm" },
      });
    }
  };

  // Fetch display mode settings
  useEffect(() => {
    const fetchDisplayMode = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl('/display-mode-settings/', false),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          setDisplayMode(response.data.docladder_display_mode || 'target_candidates');
        }
      } catch (error) {
        console.error("Error fetching display mode:", error);
      }
    };
    fetchDisplayMode();
  }, []);

  // Fetch designations for edit form
  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl('/designations/', false),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          setDesignations(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching designations:", error);
      }
    };
    fetchDesignations();
  }, []);

  // Fetch initial data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(
            getApiUrl("/Docladder/daily-user-records/", false),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          const {
            records,
            monthly_records,
            is_superuser,
            user_permissions,
            pipelineandturnedup,
          } = response.data;
          setData(records || []);
          setDailyEntryData(monthly_records || []);
          setIsSuperuser(is_superuser);
          setPipelineandturnedup(pipelineandturnedup || []);

          // Pre-fetch candidate records for all users
          const allUsers = monthly_records.map((entry) => entry.user);
          const candidateRecordsMap = {};
          const uniquePipelineCountsMap = {}; // Store precomputed unique pipeline counts
          for (const userId of allUsers) {
            const pipelineRecords = await fetchCandidateRecords(
              userId,
              "pipeline"
            );
            candidateRecordsMap[userId] = pipelineRecords;

            // Precompute unique pipeline count
            const uniquePipelines = getUniquePipelines(pipelineRecords);
            uniquePipelineCountsMap[userId] = uniquePipelines.length;
          }
          setCandidateRecords(candidateRecordsMap); // Store all candidate records
          setUniquePipelineCounts(uniquePipelineCountsMap); // Store precomputed counts

          if (is_superuser) {
            // Superuser logic
          } else if (user_permissions) {
            handlePermissionNavigation(user_permissions);
          }
        }
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
      }
    };
    fetchData();
  }, [navigate]);

  // Function to fetch candidate records for a specific user with filter
  const fetchCandidateRecords = async (userId, filterType) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.get(
        getApiUrl(`/Docladder/GetAllCandidateOfUser/${userId}/`, false),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: { status: filterType }, // Pass the filter type as a query parameter
        }
      );
      return response.data.data; // Return the fetched records
    } catch (error) {
      console.error("Error fetching candidate records:", error);
      return [];
    }
  };

  const [expandedRows, setExpandedRows] = useState(null); // Track only one expanded row at a time
  const [nestedExpandedRows, setNestedExpandedRows] = useState({}); // Track nested expanded rows per user

  // Toggle expanded row for a specific user and filter type
  const toggleExpandedRow = async (userId, filterType) => {
    if (expandedRows === userId && activeFilter[userId] === filterType) {
      // If already expanded with the same filter, collapse it
      setExpandedRows(null);
      setActiveFilter((prev) => ({ ...prev, [userId]: null }));
    } else {
      // Collapse any previously expanded row and expand the clicked row
      const records = await fetchCandidateRecords(userId, filterType);
      setCandidateRecords((prev) => ({ ...prev, [userId]: records }));
      setExpandedRows(userId); // Set the clicked row as expanded
      setActiveFilter((prev) => ({ ...prev, [userId]: filterType }));
    }
  };

  // Toggle nested expanded row for a specific record
  const toggleNestedExpandedRow = (userId, recordId) => {
    setNestedExpandedRows((prev) => {
      const updatedNestedRows = { ...prev };
      if (updatedNestedRows[userId] === recordId) {
        // If the same nested row is clicked, collapse it
        delete updatedNestedRows[userId];
      } else {
        // Collapse any previously expanded nested row and expand the clicked row
        updatedNestedRows[userId] = recordId;
      }
      return updatedNestedRows;
    });
  };

  // Sort data based on various criteria
  const sortedData = useMemo(() => {
    if (!dailyEntryData || !pipelineandturnedup || !data) return [];
    return dailyEntryData
      .map((entry) => {
        const pipeline =
          pipelineandturnedup.find((item) => item.user === entry.user)
            ?.pipline || 0;
        const turnedUpRatio =
          entry.turned_up && pipeline
            ? ((entry.selected + entry.turned_up) / entry.resume_forwarded) *
              100
            : 0;
        const correspondingRecord = data.find(
          (record) => record.id === entry.user
        );
        const targetRev = correspondingRecord ? correspondingRecord.revenue : 0;
        return {
          ...entry,
          pipeline,
          turnedUpRatio,
          targetRev,
        };
      })
      .sort((a, b) => {
        if (b.selected !== a.selected) return b.selected - a.selected;
        if (b.turnedUpRatio !== a.turnedUpRatio)
          return b.turnedUpRatio - a.turnedUpRatio;
        if (b.resume_forwarded !== a.resume_forwarded)
          return b.resume_forwarded - a.resume_forwarded;
        if (a.targetRev === 0 && b.targetRev !== 0) return 1;
        if (b.targetRev === 0 && a.targetRev !== 0) return -1;
        if (b.revenue !== a.revenue) return b.revenue - a.revenue;
        return b.pipeline - a.pipeline;
      });
  }, [dailyEntryData, pipelineandturnedup, data]);

  // Calculate totals for each column
  const totalForwarded = sortedData.reduce(
    (acc, curr) => acc + curr.resume_forwarded,
    0
  );
  const totalPipeline = sortedData.reduce(
    (acc, curr) => acc + curr.pipeline,
    0
  );
  const totalTurnedUp = sortedData.reduce(
    (acc, curr) => acc + curr.turned_up,
    0
  );
  const totalSelected = sortedData.reduce(
    (acc, curr) => acc + curr.selected,
    0
  );
  const totalJoined = sortedData.reduce(
    (acc, curr) => acc + (curr.joined || 0),
    0
  );
  const totalBackout = sortedData.reduce(
    (acc, curr) => acc + (curr.backout || 0),
    0
  );
  const totalRevenue = sortedData.reduce(
    (acc, curr) => acc + (isNaN(curr.revenue) ? 0 : parseFloat(curr.revenue)),
    0
  );
  const totalTargetRevenue = sortedData.reduce((acc, curr) => {
    const correspondingRecord = data.find((record) => record.id === curr.user);
    return acc + (correspondingRecord?.revenue || 0);
  }, 0);
  const totalTargetCandidates = sortedData.reduce((acc, curr) => {
    const correspondingRecord = data.find((record) => record.id === curr.user);
    return acc + (correspondingRecord?.target || 0);
  }, 0);
  const totalPercentage = displayMode === 'target_candidates' 
    ? (totalTargetCandidates > 0 ? (totalSelected / totalTargetCandidates) * 100 : 0)
    : (totalTargetRevenue > 0 ? (totalRevenue / totalTargetRevenue) * 100 : 0);

  // Helper function to filter unique pipelines for each candidate
  const getUniquePipelines = (records) => {
    const seenCombinations = new Set(); // To track unique combinations
    return records.filter((record) => {
      const combinationKey = `${record.hospital}-${record.city}-${record.state}-${record.speciality}`;
      if (seenCombinations.has(combinationKey)) {
        return false; // Skip duplicate
      }
      seenCombinations.add(combinationKey); // Mark as seen
      return true; // Include unique record
    });
  };

  // Handle update target
  const handleUpdateTarget = async () => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.put(
        getApiUrl(`/UpdateDocladderMemberTarget/${updateTargetUser.id}/`),
        {
          target: parseInt(targetInput),
          revenue_target: parseFloat(revenueTargetInput)
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      alert(response.data.message || 'Target updated successfully!');
      setUpdateTargetUser(null);
      // Refresh the data after update
      window.location.reload();
    } catch (error) {
      console.error("Error updating target:", error);
      alert(error.response?.data?.message || "Error updating target. Please try again.");
    }
  };

  // Open update target modal
  const openUpdateTargetModal = (userId, userName, currentTarget, currentRevenue) => {
    setUpdateTargetUser({ id: userId, name: userName });
    setTargetInput(currentTarget || '');
    setRevenueTargetInput(currentRevenue || '');
  };

  // Handle display mode change
  const handleDisplayModeChange = async (newMode) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.put(
        getApiUrl('/display-mode-settings/update/'),
        {
          docladder_display_mode: newMode
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      setDisplayMode(newMode);
      alert(response.data.message || 'Display mode updated successfully!');
    } catch (error) {
      console.error("Error updating display mode:", error);
      alert(error.response?.data?.message || "Error updating display mode. Please try again.");
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.delete(
        getApiUrl(`/Docladder/DelMember/${userId}/`),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      // Refresh the data after deletion
      window.location.reload();
    } catch (error) {
      console.error("Error deleting member:", error);
      alert(error.response?.data?.message || "Error deleting member. Please try again.");
    }
    setDeleteConfirmUser(null);
  };

  // Open edit user modal
  const openEditUserModal = (userId, userName, userDesignation) => {
    setEditUser({ id: userId, name: userName });
    setEditName(userName || '');
    setEditDesignation(userDesignation || '');
    setEditImage(null);
  };

  // Handle edit user details
  const handleEditUser = async () => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const formData = new FormData();
      
      if (editName) formData.append('name', editName);
      if (editDesignation) formData.append('des', editDesignation);
      if (editImage) formData.append('image', editImage);
      
      const response = await axios.put(
        getApiUrl(`/UpdateDocladderMemberDetails/${editUser.id}/`),
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      
      alert(response.data.message || 'Member details updated successfully!');
      setEditUser(null);
      // Refresh the data after update
      window.location.reload();
    } catch (error) {
      console.error("Error updating member details:", error);
      alert(error.response?.data?.message || "Error updating member details. Please try again.");
    }
  };

  return (
    <>
      <section style={{ maxHeight: "100vh" }}>
        <div style={{ display: "flex" }}>
          <CustomNavbar />
          <div
            style={{
              width: "99vw",
              border: "1px solid #ddd",
              padding: "0 10px 10px 10px",
            }}
          >
            <div className="flex justify-between items-center mb-4" style={{ marginLeft: "30px", marginRight: "30px", marginTop: "10px" }}>
              <h2 className="text-blue-500 text-2xl font-bold">
                Docladder Team Records
              </h2>
              <div className="flex gap-4 items-center">
                {isSuperuser && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Display Mode:</label>
                    <select
                      value={displayMode}
                      onChange={(e) => handleDisplayModeChange(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="target_candidates">Target & Candidates Closed</option>
                      <option value="revenue">Revenue Target & Revenue Generated</option>
                    </select>
                  </div>
                )}
                <button
                  onClick={() => {
                    const exportData = sortedData.map((entry) => {
                      const user = data.find((r) => r.id === entry.user);
                      return {
                        name: user?.name || "",
                        username: user?.username || "",
                        resume_forwarded: entry.resume_forwarded || 0,
                        pipeline_unique: uniquePipelineCounts[entry.user] || 0,
                        pipeline: entry.pipeline || 0,
                        turned_up: entry.turned_up || 0,
                        selected: entry.selected || 0,
                        joined: entry.joined || 0,
                        backout: entry.backout || 0,
                        revenue: entry.revenue || 0,
                        target: user?.revenue || 0,
                      };
                    });
                    exportRecordsToExcel(exportData, `Docladder_Records_${new Date().toISOString().split('T')[0]}`);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  📥 Export to Excel
                </button>
              </div>
            </div>
            <table style={{ width: "100%", backgroundColor: "white" }}>
              <thead>
                <tr
                  className="bg-blue-500"
                  style={{
                    position: "sticky",
                    top: "0",
                    fontSize: "16px",
                  }}
                >
                  <th className="py-2 px-4 border-b">Images</th>
                  <th className="py-2 px-4 border-b">Name</th>
                  <th className="py-2 px-4 border-b">Forwarded</th>
                  <th className="py-2 px-4 border-b">Pipeline</th>
                  <th className="py-2 px-4 border-b">Turned Up</th>
                  <th className="py-2 px-4 border-b">
                    {displayMode === 'target_candidates' ? 'Selected Candidate' : 'Selected This Month'}
                  </th>
                  <th className="py-2 px-4 border-b">Joined</th>
                  <th className="py-2 px-4 border-b">Backout</th>
                  <th className="py-2 px-4 border-b">
                    {displayMode === 'target_candidates' ? 'Target / Achieved' : 'Revenue'}
                  </th>
                  <th className="py-2 px-4 border-b">Achievement Ratio</th>
                  {isSuperuser && <th className="py-2 px-4 border-b">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-300">
                {sortedData.map((dailyEntry) => {
                  const correspondingRecord = data.find(
                    (record) => record.id === dailyEntry.user
                  );
                  const imageSrc = correspondingRecord
                    ? `${API_BASE_URL}${correspondingRecord.image}`
                    : "";
                  const userName = correspondingRecord
                    ? correspondingRecord.name
                    : "";
                  const targetRev = correspondingRecord
                    ? correspondingRecord.revenue
                    : "";
                  const pipelineData =
                    pipelineandturnedup.find(
                      (entry) => entry.user === dailyEntry.user
                    );
                  const pipeline = pipelineData?.pipeline || pipelineData?.pipline || 0;
                  const pipelineUnique = pipelineData?.pipeline_unique || uniquePipelineCounts[dailyEntry.user] || 0;
                  const isExpanded = expandedRows === dailyEntry.user;

                  // Get unique pipeline count for the user
                  const uniqueCandidateRecords = getUniquePipelines(
                    candidateRecords[dailyEntry.user] || []
                  );

                  return (
                    <React.Fragment key={dailyEntry.user}>
                      {/* Main Row */}
                      <tr style={{ fontSize: "14px" }}>
                        <td className="py-2 px-4 border-b text-center">
                          {imageSrc && (
                            <img
                              src={imageSrc}
                              className="w-auto"
                              style={{ height: "100px" }}
                              alt=""
                            />
                          )}
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {userName}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(
                              dailyEntry.user,
                              "resume_forwarded"
                            )
                          }
                        >
                          {dailyEntry.resume_forwarded}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "pipeline")
                          }
                        >
                          {pipelineUnique} / {pipeline === "" ? "0" : pipeline}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "turned_up")
                          }
                        >
                          {dailyEntry.turned_up}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "selected")
                          }
                        >
                          {dailyEntry.selected}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "joined")
                          }
                        >
                          {dailyEntry.joined || 0}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() =>
                            toggleExpandedRow(dailyEntry.user, "backout")
                          }
                        >
                          {dailyEntry.backout || 0}
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {displayMode === 'target_candidates' 
                            ? `${correspondingRecord?.target || 0} / ${dailyEntry.selected}`
                            : `${isNaN(dailyEntry.revenue) ? 0 : parseFloat(dailyEntry.revenue)} / ${targetRev} lac`
                          }
                        </td>
                        <td
                          className={`py-2 px-4 border-b text-center ${
                            (dailyEntry.revenue / targetRev) * 100 <= 50
                              ? "text-red-500"
                              : (dailyEntry.revenue / targetRev) * 100 < 91
                              ? "text-yellow-500"
                              : (dailyEntry.revenue / targetRev) * 100 < 115
                              ? "text-green-500"
                              : "text-blue-700"
                          }`}
                        >
                          {dailyEntry.revenue !== 0 ? (
                            ((dailyEntry.revenue / targetRev) * 100).toFixed(2)
                          ) : (
                            <span className="text-red-500">0</span>
                          )}
                          %
                        </td>
                        {isSuperuser && (
                          <td className="py-2 px-4 border-b text-center">
                            <div className="flex gap-2 justify-center flex-wrap">
                              <button
                                onClick={() => openEditUserModal(dailyEntry.user, userName, correspondingRecord?.des?.id)}
                                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Edit Details"
                              >
                                📝 Edit
                              </button>
                              <button
                                onClick={() => openUpdateTargetModal(dailyEntry.user, userName, dailyEntry.selected, targetRev)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Update Target"
                              >
                                🎯 Target
                              </button>
                              <button
                                onClick={() => setDeleteConfirmUser({ id: dailyEntry.user, name: userName })}
                                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Delete Member"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {/* Extended Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={isSuperuser ? "10" : "9"} className="p-4 bg-gray-100">
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                              }}
                            >
                              <thead className="bg-gray-300">
                                <tr>
                                  <th className="py-2 px-4 border-b">
                                    Candidate Name
                                  </th>
                                  <th className="py-2 px-4 border-b">
                                    Hospital
                                  </th>
                                  <th className="py-2 px-4 border-b">City</th>
                                  <th className="py-2 px-4 border-b">State</th>
                                  <th className="py-2 px-4 border-b">
                                    Speciality
                                  </th>
                                  <th className="py-2 px-4 border-b">Status</th>
                                  <th className="py-2 px-4 border-b">
                                    Revenue
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {candidateRecords[dailyEntry.user]?.map(
                                  (record) => {
                                    const isNestedExpanded =
                                      nestedExpandedRows[dailyEntry.user] ===
                                      record.id;
                                    return (
                                      <React.Fragment key={record.id}>
                                        <tr
                                          onClick={() =>
                                            toggleNestedExpandedRow(
                                              dailyEntry.user,
                                              record.id
                                            )
                                          }
                                          style={{
                                            cursor: "pointer",
                                            backgroundColor: isNestedExpanded
                                              ? "#f0f8ff"
                                              : "transparent",
                                            padding: "35px",
                                          }}
                                        >
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.candidate_name}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.hospital}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.city}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.state}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {record.speciality}
                                          </td>
                                          <td
                                            className="py-4 px-4 border-b text-center"
                                            style={{
                                              color:
                                                record.status ===
                                                "resume_forwarded"
                                                  ? "green"
                                                  : record.status === "pipeline"
                                                  ? "green" // Color for "pipeline"
                                                  : record.status ===
                                                    "turned_up"
                                                  ? "orange" // Color for "turned_up"
                                                  : record.status === "backout"
                                                  ? "red" // Color for "backout"
                                                  : record.status ===
                                                    "not selected"
                                                  ? "gray" // Color for "not selected"
                                                  : record.status === "selected"
                                                  ? "blue"
                                                  : record.status === "joined" ? "green"
                                                  : "black", //
                                            }}
                                          >
                                            {record.status}
                                          </td>
                                          <td className="py-4 px-4 border-b text-center">
                                            {isNaN(record.revenue) ? 0 : parseFloat(record.revenue)} lac

                                          </td>
                                        </tr>
                                        {/* Nested Expanded Row */}
                                        {isNestedExpanded && (
                                          <tr
                                            style={{
                                              backgroundColor: "#96DED1",
                                              border: "3px solid #f0f8ff",
                                              borderBottom: "2px solid 	#E8E8E8",
                                            }}
                                          >
                                            <td
                                              colSpan="9"
                                              className="p-3"
                                              style={{
                                                backgroundColor: "#96DED1",
                                              }}
                                            >
                                              <div>
                                                <strong>Remark:</strong>{" "}
                                                {record.remark}
                                              </div>
                                              <div>
                                                <strong>Date Entered:</strong>{" "}
                                                {record.date_entered}
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  }
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Totals Row */}
                <tr className="bg-gray-300" style={{ fontSize: "17px" }}>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    Total
                  </td>
                  <td className="py-2 px-4 border-b"></td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalForwarded}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalPipeline}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalTurnedUp}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalSelected}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalJoined}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    {totalBackout}
                  </td>
                  <td className="py-2 px-4 border-b text-center font-bold ">
                    {displayMode === 'target_candidates' ? (
                      <>
                        <span
                          className={` ${
                            totalPercentage <= 50
                              ? "text-red-500"
                              : totalPercentage < 91
                              ? "text-yellow-500"
                              : totalPercentage < 115
                              ? "text-green-500"
                              : "text-blue-700"
                          }`}
                        >
                          {totalSelected}{" "}
                        </span>
                        / {totalTargetCandidates}
                      </>
                    ) : (
                      <>
                        <span
                          className={` ${
                            totalPercentage <= 50
                              ? "text-red-500"
                              : totalPercentage < 91
                              ? "text-yellow-500"
                              : totalPercentage < 115
                              ? "text-green-500"
                              : "text-blue-700"
                          }`}
                        >
                          {totalRevenue.toFixed(2)}{" "}
                        </span>
                        / {totalTargetRevenue.toFixed(2)} lac
                      </>
                    )}
                  </td>
                  <td
                    className={`py-2 px-4 border-b text-center font-bold ${
                      totalPercentage <= 50
                        ? "text-red-500"
                        : totalPercentage < 91
                        ? "text-yellow-500"
                        : totalPercentage * 100 < 115
                        ? "text-green-500"
                        : "text-blue-700"
                    }`}
                  >
                    {totalPercentage.toFixed(2)}%
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Edit User Modal */}
            {editUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold text-green-600 mb-4">
                    📝 Edit Member Details - {editUser.name}
                  </h3>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name:
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Enter member name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Designation:
                      </label>
                      <select
                        value={editDesignation}
                        onChange={(e) => setEditDesignation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Select Designation</option>
                        {designations.map((des) => (
                          <option key={des.id} value={des.id}>
                            {des.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Image:
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setEditImage(e.target.files[0])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to keep current image</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setEditUser(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditUser}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
                      disabled={!editName}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold text-red-600 mb-4">
                    ⚠️ Confirm Deletion
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Are you sure you want to delete member <strong>{deleteConfirmUser.name}</strong>?
                    <br />
                    <span className="text-red-500 text-sm">
                      This action cannot be undone. All associated data will be permanently deleted.
                    </span>
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setDeleteConfirmUser(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteUser(deleteConfirmUser.id)}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Update Target Modal */}
            {updateTargetUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold text-blue-600 mb-4">
                    🎯 Update Target for {updateTargetUser.name}
                  </h3>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target (Selected Candidates):
                      </label>
                      <input
                        type="number"
                        value={targetInput}
                        onChange={(e) => setTargetInput(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter target number"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Revenue Target (in Lac):
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={revenueTargetInput}
                        onChange={(e) => setRevenueTargetInput(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter revenue target"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setUpdateTargetUser(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateTarget}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                      disabled={!targetInput || !revenueTargetInput}
                    >
                      Update Target
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default Records;
